// ColorPicker.tsx
import React, { FunctionComponent } from "react";
import styled from "styled-components";
import { HexColorPicker, HexAlphaColorPicker, HexColorInput } from "react-colorful";
import Fonts from "../global/Fonts";
import Colors from "../global/Colors";
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';

export type ColorMode = "light" | "dark";

const ColorPickerRoot = styled.div<{ $colorMode: ColorMode }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-width: 200px;
  max-width: 300px;
  background-color: ${({ $colorMode }) =>
    $colorMode === "dark" ? Colors.grey9 : Colors.white};
  border-radius: 4px;
  border: 1px solid ${props => props.$colorMode === 'dark' ? Colors.grey8 : Colors.grey1};
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  box-sizing: border-box;
  padding: 8px;
  gap: 8px;
`;

const Title = styled.label<{ $colorMode: ColorMode }>`
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  font-size: 16px;
  color: ${({ $colorMode }) =>
    $colorMode === "dark" ? Colors.white : Colors.grey9};
  text-align: center;
  user-select: none;
`;

const StyledColorPicker = styled.div`
  width: 100% !important;
  height: auto !important;

  .react-colorful__interactive {
    border: 1px solid ${Colors.grey5};
  }

  .react-colorful__saturation,
  .react-colorful__hue,
  .react-colorful__alpha {
    border-radius: 4px;
  }

  .react-colorful__saturation {
    padding-bottom: 75% !important;
    border-width: 1px;
  }

  .react-colorful__hue,
  .react-colorful__alpha {
    height: 10px !important;
  }

  .react-colorful__hue {
    margin-top: 12px;
  }

  .react-colorful__alpha {
    margin-top: 12px;
  }
`;

const ColorInputContainer = styled.div<{ $colorMode?: ColorMode }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  font-size: 14px;
  margin: 10px auto 0 auto;

  padding: 2px;
  border-radius: 6px;
  background-color: ${({ $colorMode }) =>
    $colorMode === "dark" ? Colors.grey9 : Colors.white};
  border: 1px solid ${({ $colorMode }) =>
    $colorMode === "dark" ? Colors.grey8 : Colors.grey3};

  &:hover, &:focus-within {
    border: 1px solid ${Colors.grey5};
  }
`;

const ColorDisplaySwatch = styled.div<{ color: string }>`
  width: 18px;
  height: 18px;
  background-color: ${({ color }) => color};
  border-radius: 4px;
  border: 1px solid ${Colors.grey5};
  flex-shrink: 0;
  margin: 0 6px 0 4px;
`;

const HexPrefix = styled.span<{ $colorMode: ColorMode }>`
  color: ${({ $colorMode }) =>
    $colorMode === "dark" ? Colors.grey2 : Colors.grey8};
  font-family: ${Fonts.lexendLight.fontFamily};
  font-weight: ${Fonts.lexendLight.fontWeight};
  font-size: 14px;
`;

const StyledHexColorInput = styled(HexColorInput)<{ $colorMode?: ColorMode }>`
  width: 58px;
  box-sizing: border-box;
  border: none;
  outline: none;
  border-radius: 4px;
  font-family: ${Fonts.lexendLight.fontFamily};
  font-weight: ${Fonts.lexendLight.fontWeight};
  font-size: 14px;
  text-align: left;
  text-transform: uppercase;
  background-color: ${Colors.transparent};
  color: ${({ $colorMode }) =>
    $colorMode === "dark" ? Colors.grey2 : Colors.grey8};
`;

export type ColorPickerProps = {
  color: string; // hex
  onChange: (newColor: string) => void;
  label?: string;
  colorMode?: ColorMode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorEl: HTMLElement | null;
  alpha?: boolean;
};

const ColorPicker: FunctionComponent<ColorPickerProps> = ({
  color,
  onChange,
  label,
  colorMode = "dark",
  open,
  onOpenChange,
  anchorEl,
  alpha = false,
}) => {
  const handleClickAway = () => {
    onOpenChange(false);
  };

  return (
    <Popper open={open} anchorEl={anchorEl} placement="left" sx={{ padding: '10px', zIndex: 1500 }}>
      <ClickAwayListener onClickAway={handleClickAway}>
        <ColorPickerRoot $colorMode={colorMode}>
          {label && <Title $colorMode={colorMode}>{label}</Title>}
          <StyledColorPicker>
            {alpha ? (
              <HexAlphaColorPicker color={color} onChange={onChange} />
            ) : (
              <HexColorPicker color={color} onChange={onChange} />
            )}
          </StyledColorPicker>
          <ColorInputContainer $colorMode={colorMode}>
            <ColorDisplaySwatch color={color} />
            <HexPrefix $colorMode={colorMode}>#</HexPrefix>
            <StyledHexColorInput
              color={color} // Pass the color prop directly
              onChange={onChange} // Use the component's onChange
              $colorMode={colorMode}
              alpha={alpha} // Enable alpha channel for the input based on prop
            />
          </ColorInputContainer>
        </ColorPickerRoot>
      </ClickAwayListener>
    </Popper>
  );
};

export default ColorPicker;